<template>
  <div class="container-fluid p-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center">
            <button class="btn btn-outline-secondary me-3" @click="goBack">
              <i class="bi bi-arrow-left me-2"></i>Back
            </button>
            <h2 class="mb-0 fw-bold gradient-text">Challenge Details</h2>
          </div>
          <div class="d-flex gap-2">
            <button 
              class="btn btn-primary" 
              @click="joinChallenge"
              v-if="challenge && !challenge.joined && !challenge.completed"
            >
              <i class="bi bi-plus-lg me-2"></i>Join Challenge
            </button>
            <button 
              class="btn btn-success" 
              @click="completeChallenge"
              v-if="challenge && challenge.joined && !challenge.completed"
            >
              <i class="bi bi-check-lg me-2"></i>Complete Challenge
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading challenge details...</p>
    </div>

    <!-- Challenge Content -->
    <div v-else-if="challenge" class="row">
      <!-- Main Challenge Info -->
      <div class="col-lg-8 mb-4">
        <!-- Challenge Header -->
        <div class="modern-card p-4 mb-4">
          <div class="d-flex align-items-start justify-content-between mb-4">
            <div class="flex-grow-1">
              <h3 class="fw-bold mb-2">{{ challenge.title }}</h3>
              <p class="text-secondary mb-3">{{ challenge.description }}</p>
              
              <div class="d-flex align-items-center gap-3 mb-3">
                <span 
                  class="badge" 
                  :class="getDifficultyBadgeClass(challenge.difficulty)"
                >
                  {{ challenge.difficulty }}
                </span>
                <span class="text-muted">
                  <i class="bi bi-clock me-1"></i>
                  {{ challenge.duration }}
                </span>
                <span class="text-muted">
                  <i class="bi bi-people me-1"></i>
                  {{ challenge.participants }} participants
                </span>
              </div>
            </div>
            
            <div class="challenge-icon">
              <i :class="challenge.icon" style="font-size: 3rem; color: var(--role-primary);"></i>
            </div>
          </div>

          <!-- Progress Bar -->
          <div class="mb-4" v-if="challenge.joined">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="fw-bold">Progress</span>
              <span class="text-muted">{{ challenge.progress }}%</span>
            </div>
            <div class="progress">
              <div 
                class="progress-bar bg-success" 
                :style="{ width: challenge.progress + '%' }"
              ></div>
            </div>
          </div>

          <!-- Challenge Status -->
          <div class="alert" :class="getStatusAlertClass()" v-if="challenge.joined">
            <i :class="getStatusIcon()" class="me-2"></i>
            {{ getStatusMessage() }}
          </div>
        </div>

        <!-- Challenge Goals -->
        <div class="modern-card p-4 mb-4">
          <h5 class="fw-bold mb-4">Challenge Goals</h5>
          <div class="goal-list">
            <div 
              v-for="goal in challenge.goals" 
              :key="goal.id"
              class="goal-item d-flex align-items-center mb-3 p-3 rounded"
              :class="{ 'bg-success bg-opacity-10': goal.completed }"
              @click="toggleGoal(goal)"
            >
              <div class="goal-icon me-3">
                <i 
                  :class="goal.completed ? 'bi bi-check-circle-fill text-success' : 'bi bi-circle text-muted'"
                  style="font-size: 1.5rem;"
                ></i>
              </div>
              <div class="flex-grow-1">
                <div class="fw-bold">{{ goal.title }}</div>
                <small class="text-muted">{{ goal.description }}</small>
              </div>
              <div class="goal-progress" v-if="goal.target">
                <span class="badge bg-primary">{{ goal.current || 0 }}/{{ goal.target }}</span>
                <button class="btn btn-sm btn-outline-primary ms-2"
                        @click.stop="incrementGoalToday(goal)"
                        :disabled="goal.locked">
                  +1 Today
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Challenge Activities -->
        <div class="modern-card p-4 mb-4">
          <h5 class="fw-bold mb-4">Activities</h5>
          <div class="activity-grid">
            <div 
              v-for="activity in challenge.activities" 
              :key="activity.id"
              class="activity-card modern-card p-3 mb-3 cursor-pointer"
              @click="startActivity(activity)"
              :class="{ 'completed': activity.completed, 'locked': activity.locked }"
            >
              <div class="d-flex align-items-center">
                <div class="activity-icon me-3">
                  <i :class="getActivityIcon(activity)" style="font-size: 1.5rem;"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-bold">{{ activity.title }}</div>
                  <small class="text-muted">{{ activity.description }}</small>
                </div>
                <div class="d-flex align-items-center gap-2 ms-3">
                  <button 
                    class="btn btn-sm btn-outline-success"
                    v-if="!activity.completed && !activity.locked"
                    @click.stop="markActivityComplete(activity)"
                  >
                    Mark Done
                  </button>
                  <div class="activity-status">
                  <i 
                    v-if="activity.completed"
                    class="bi bi-check-circle-fill text-success"
                  ></i>
                  <i 
                    v-else-if="activity.locked"
                    class="bi bi-lock-fill text-muted"
                  ></i>
                  <i 
                    v-else
                    class="bi bi-play-circle text-primary"
                  ></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Leaderboard -->
        <div class="modern-card p-4">
          <h5 class="fw-bold mb-4">Leaderboard</h5>
          <div class="leaderboard-list">
            <div 
              v-for="(participant, index) in challenge.leaderboard" 
              :key="participant.id"
              class="leaderboard-item d-flex align-items-center mb-3 p-3 rounded"
              :class="{ 'bg-warning bg-opacity-10': participant.isCurrentUser }"
            >
              <div class="rank me-3">
                <span 
                  class="badge"
                  :class="getRankBadgeClass(index + 1)"
                >
                  {{ index + 1 }}
                </span>
              </div>
              <div class="participant-avatar me-3">
                {{ participant.name.charAt(0) }}
              </div>
              <div class="flex-grow-1">
                <div class="fw-bold">
                  {{ participant.name }}
                  <span v-if="participant.isCurrentUser" class="text-primary">(You)</span>
                </div>
                <small class="text-muted">{{ participant.points }} points</small>
              </div>
              <div class="participant-progress">
                <div class="progress" style="width: 100px; height: 6px;">
                  <div 
                    class="progress-bar bg-primary" 
                    :style="{ width: participant.progress + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="col-lg-4">
        <!-- Challenge Info -->
        <div class="modern-card p-4 mb-4">
          <h6 class="fw-bold mb-3">Challenge Information</h6>
          
          <div class="info-item mb-3">
            <small class="text-muted d-block">Duration</small>
            <span class="fw-bold">{{ challenge.duration }}</span>
          </div>
          
          <div class="info-item mb-3">
            <small class="text-muted d-block">Difficulty</small>
            <span class="fw-bold">{{ challenge.difficulty }}</span>
          </div>
          
          <div class="info-item mb-3">
            <small class="text-muted d-block">Category</small>
            <span class="fw-bold">{{ challenge.category }}</span>
          </div>
          
          <div class="info-item mb-3">
            <small class="text-muted d-block">Start Date</small>
            <span class="fw-bold">{{ formatDate(challenge.startDate) }}</span>
          </div>
          
          <div class="info-item">
            <small class="text-muted d-block">End Date</small>
            <span class="fw-bold">{{ formatDate(challenge.endDate) }}</span>
          </div>
        </div>

        <!-- Rewards -->
        <div class="modern-card p-4 mb-4">
          <h6 class="fw-bold mb-3">Rewards</h6>
          <div class="reward-list">
            <div 
              v-for="reward in challenge.rewards" 
              :key="reward.id"
              class="reward-item d-flex align-items-center mb-2"
            >
              <i :class="reward.icon" class="me-2 text-warning"></i>
              <span>{{ reward.title }}</span>
            </div>
          </div>
        </div>

        <!-- Challenge Tips -->
        <div class="modern-card p-4">
          <h6 class="fw-bold mb-3">Tips for Success</h6>
          <ul class="list-unstyled">
            <li v-for="tip in challenge.tips" :key="tip" class="mb-2">
              <i class="bi bi-lightbulb text-warning me-2"></i>
              {{ tip }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="text-center py-5">
      <i class="bi bi-exclamation-triangle text-warning" style="font-size: 3rem;"></i>
      <h5 class="mt-3">Challenge Not Found</h5>
      <p class="text-muted">The requested challenge could not be loaded.</p>
      <button class="btn btn-primary" @click="goBack">Go Back</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChallengeDetail',
  data() {
    return {
      loading: true,
      challenge: null,
      challengeId: null
    }
  },
  mounted() {
    this.challengeId = this.$route.params.id || this.$route.query.challengeId
    this.loadChallengeDetails()
  },
  methods: {
    async loadChallengeDetails() {
      try {
        this.loading = true
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id
        if (!userId) {
          throw new Error('Missing current user')
        }
        const { getChallengeById, getChallengeLeaderboard } = await import('../services/challengeService.js')
        const res = await getChallengeById(this.challengeId, userId)
        const data = res?.data || res
        this.challenge = {
          id: data.id,
          title: data.title,
          description: data.description,
          icon: data.icon || 'bi bi-trophy',
          difficulty: data.difficulty || 'Beginner',
          duration: data.duration || '',
          category: data.category || '',
          participants: data.participants || 0,
          joined: !!data.joined,
          completed: !!data.completed,
          progress: data.progress ?? 0,
          startDate: data.startDate,
          endDate: data.endDate,
          goals: data.goals || [],
          activities: data.activities || [],
          rewards: data.rewards || [],
          tips: data.tips || []
        }
        // Optionally load leaderboard
        try {
          const lb = await getChallengeLeaderboard(this.challengeId, userId)
          this.challenge.leaderboard = (lb?.data) || []
        } catch (e) {
          this.challenge.leaderboard = []
        }
      } catch (e) {
        console.error('Failed to load challenge details:', e)
        this.challenge = null
      } finally {
        this.loading = false
      }
    },

    async joinChallenge() {
      try {
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id
        const { joinChallenge } = await import('../services/challengeService.js')
        await joinChallenge(this.challenge.id, userId)
        this.challenge.joined = true
        this.showMessage('Successfully joined the challenge!')
      } catch (e) {
        console.error('Join failed:', e)
        this.showMessage('Could not join challenge. Please try again.')
      }
    },

    async completeChallenge() {
      try {
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id
        const { updateChallengeProgress } = await import('../services/challengeService.js')
        await updateChallengeProgress(this.challenge.id, userId, { completed: true })
        this.challenge.completed = true
        this.challenge.progress = 100
        this.showMessage('Congratulations! Challenge completed!')
      } catch (e) {
        console.error('Complete failed:', e)
        this.showMessage('Could not complete challenge. Please try again.')
      }
    },

    startActivity(activity) {
      if (activity.locked) {
        this.showMessage('This activity is locked. Complete previous activities first.')
        return
      }
      if (activity.completed) {
        this.showMessage('This activity has already been completed.')
        return
      }

      // Smart routing based on activity title/type/category
      const title = (activity.title || '').toLowerCase()
      const type = (activity.type || '').toLowerCase()
      const category = (this.challenge?.category || '').toLowerCase()

      // Map to existing app pages
      if (title.includes('read') || category.includes('reading')) {
        this.$router.push('/library')
        return
      }
      if (title.includes('word') || title.includes('vocabulary') || type === 'vocabulary' || category.includes('language')) {
        this.$router.push('/language-learning')
        return
      }
      if (title.includes('complete') || title.includes('activity') || type === 'goal') {
        // General learning activities → Activity hub
        this.$router.push('/activity')
        return
      }
      if (title.includes('share')) {
        this.$router.push('/profile')
        return
      }

      // Fallback
      this.$router.push('/student-dashboard')
    },

    getDifficultyBadgeClass(difficulty) {
      const classes = {
        'Beginner': 'bg-success',
        'Intermediate': 'bg-warning',
        'Advanced': 'bg-danger'
      }
      return classes[difficulty] || 'bg-secondary'
    },

    getStatusAlertClass() {
      if (this.challenge.completed) return 'alert-success'
      if (this.challenge.joined) return 'alert-info'
      return 'alert-secondary'
    },

    getStatusIcon() {
      if (this.challenge.completed) return 'bi bi-check-circle-fill'
      if (this.challenge.joined) return 'bi bi-info-circle-fill'
      return 'bi bi-circle'
    },

    getStatusMessage() {
      if (this.challenge.completed) return 'Challenge completed! Great job!'
      if (this.challenge.joined) return 'You are participating in this challenge'
      return 'Join this challenge to start'
    },

    getActivityIcon(activity) {
      const icons = {
        reading: 'bi bi-book text-primary',
        vocabulary: 'bi bi-chat-square-text text-info',
        writing: 'bi bi-pencil text-warning',
        quiz: 'bi bi-question-circle text-success'
      }
      
      if (activity.completed) return 'bi bi-check-circle-fill text-success'
      if (activity.locked) return 'bi bi-lock-fill text-muted'
      
      return icons[activity.type] || 'bi bi-circle text-primary'
    },

    getRankBadgeClass(rank) {
      if (rank === 1) return 'bg-warning'
      if (rank === 2) return 'bg-secondary'
      if (rank === 3) return 'bg-dark'
      return 'bg-primary'
    },

    async markActivityComplete(activity) {
      try {
        activity.completed = true
        const total = (this.challenge.activities || []).length || 1
        const completedCount = (this.challenge.activities || []).filter(a => a.completed).length
        const percent = Math.min(100, Math.round((completedCount / total) * 100))
        this.challenge.progress = percent

        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id
        const { updateChallengeProgress } = await import('../services/challengeService.js')

        if (percent >= 100) {
          await updateChallengeProgress(this.challenge.id, userId, { completed: true })
          this.challenge.completed = true
          try { localStorage.setItem('challengeUpdated', '1') } catch (e) { /* ignore */ }
        } else {
          await updateChallengeProgress(this.challenge.id, userId, { progress: percent })
          try { localStorage.setItem('challengeUpdated', '1') } catch (e) { /* ignore */ }
        }
      } catch (e) {
        console.error('Failed to update challenge progress:', e)
        this.showMessage('Could not update progress. Please try again.', 'error')
      }
    },

    async toggleGoal(goal) {
      try {
        goal.completed = !goal.completed
        const total = (this.challenge.goals || []).length || 1
        const completedCount = (this.challenge.goals || []).filter(g => g.completed).length
        const percent = Math.min(100, Math.round((completedCount / total) * 100))
        this.challenge.progress = percent

        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id
        const { updateChallengeProgress } = await import('../services/challengeService.js')
        if (percent >= 100) {
          await updateChallengeProgress(this.challenge.id, userId, { completed: true })
          this.challenge.completed = true
        } else {
          await updateChallengeProgress(this.challenge.id, userId, { progress: percent })
        }
        try { localStorage.setItem('challengeUpdated', '1') } catch (e) { /* ignore */ }
      } catch (e) {
        console.error('Failed to toggle goal:', e)
      }
    },

    async incrementGoalToday(goal) {
      try {
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id
        const { incrementGoal } = await import('../services/challengeService.js')
        const res = await incrementGoal(goal.id, userId, 1)
        if (res?.success) {
          goal.current = res.data?.current || goal.current || 0
          // Recompute overall progress based on completed goals
          const totalGoals = (this.challenge.goals || []).length || 1
          const satisfied = (this.challenge.goals || []).filter(g => (g.current || 0) >= (g.target || 1)).length
          this.challenge.progress = Math.round((satisfied / totalGoals) * 100)
          if (this.challenge.progress >= 100) {
            this.challenge.completed = true
          }
        }
        try { localStorage.setItem('challengeUpdated', '1') } catch (e) { /* ignore */ }
      } catch (e) {
        console.error('Failed to increment goal:', e)
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },

    goBack() {
      this.$router.go(-1)
    },

    showMessage(message, type = 'info') {
      // Create feedback alert
      const alertClass = type === 'success' ? 'alert-success' : 
                         type === 'warning' ? 'alert-warning' :
                         type === 'error' ? 'alert-danger' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      document.body.appendChild(alert);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (alert.parentElement) {
          alert.remove();
        }
      }, 5000);
    }
  }
}
</script>

<style scoped>
.challenge-icon {
  opacity: 0.1;
  position: absolute;
  top: 20px;
  right: 20px;
}

.goal-item, .activity-card, .leaderboard-item {
  transition: all 0.2s ease;
  border: 1px solid var(--border-color);
}

.goal-item:hover, .activity-card:hover, .leaderboard-item:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow);
}

.activity-card.completed {
  background-color: var(--bg-secondary);
  opacity: 0.8;
}

.activity-card.locked {
  opacity: 0.5;
  cursor: not-allowed;
}

.participant-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--role-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.cursor-pointer {
  cursor: pointer;
}

.progress {
  height: 8px;
}

.info-item small {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style> 